import dayjs from 'dayjs';
import z from 'zod';

export const dispatchScopeSchema = z.enum(['PROJECT', 'SHOP']);
export const dispatchReasonSchema = z.enum([
  'PROJECT_DELIVERY',
  'RETURN_VENDOR',
  'SCRAP',
  'OTHER',
]);

const dispatchLineFields = {
  quantity: z.coerce
    .number()
    .positive({ error: 'Enter a quantity greater than zero.' }),
  notes: z.string().trim().max(500).optional().default(''),
  item_code: z.string().trim().optional().default(''),
  item_name: z.string().trim().max(255).optional().default(''),
  description: z.string().trim().max(2000).optional().default(''),
  unit: z.string().trim().optional().default(''),
  available_qty: z.coerce.number().min(0).optional().default(0),
};

export const dispatchLineSchema = z.discriminatedUnion('entry_mode', [
  z.object({
    entry_mode: z.literal('stock'),
    inventory: z.coerce
      .number()
      .int()
      .positive({ error: 'Choose an inventory item.' }),
    ...dispatchLineFields,
  }),
  z.object({
    entry_mode: z.literal('manual'),
    inventory: z.null().optional(),
    ...dispatchLineFields,
    item_name: z
      .string()
      .trim()
      .min(1, { error: 'Enter an item name.' })
      .max(255),
  }),
]);

export const stockDispatchDraftSchema = z
  .object({
    scope: dispatchScopeSchema,
    project: z.coerce.number().int().positive().nullable().optional(),
    destination_project: z.coerce
      .number()
      .int()
      .positive()
      .nullable()
      .optional(),
    shipment: z.coerce.number().int().positive().nullable().optional(),
    reason: dispatchReasonSchema,
    reference: z.string().trim().max(255).optional().default(''),
    destination: z.string().trim().max(255).optional().default(''),
    recipient: z.string().trim().max(255).optional().default(''),
    planned_date: z
      .string()
      .min(1, { error: 'Choose a planned dispatch date.' })
      .refine((value) => dayjs(value).isValid(), {
        error: 'Choose a valid planned dispatch date.',
      }),
    notes: z.string().trim().max(1000).optional().default(''),
    lines: z
      .array(dispatchLineSchema)
      .min(1, { error: 'Add at least one stock item.' }),
  })
  .superRefine((draft, context) => {
    if (draft.scope === 'PROJECT' && !draft.project) {
      context.addIssue({
        code: 'custom',
        path: ['project'],
        message: 'Select a project before dispatching project stock.',
      });
    }

    const inventoryIds = new Set<number>();
    draft.lines.forEach((line, index) => {
      if (line.entry_mode !== 'stock') return;
      if (inventoryIds.has(line.inventory)) {
        context.addIssue({
          code: 'custom',
          path: ['lines', index, 'inventory'],
          message: 'This item appears more than once.',
        });
      }
      inventoryIds.add(line.inventory);

      if (line.quantity > line.available_qty) {
        context.addIssue({
          code: 'custom',
          path: ['lines', index, 'quantity'],
          message: `Only ${line.available_qty} ${line.unit} available.`,
        });
      }
    });
  });

export const stockDispatchVoidSchema = z.object({
  void_reason: z
    .string()
    .trim()
    .min(1, { error: 'Enter the reason for voiding this dispatch.' })
    .max(1000),
});

export type StockDispatchDraftFormValues = z.infer<
  typeof stockDispatchDraftSchema
>;
